'use client';

import { useState } from 'react';
import ToolHero from '@/components/tools/ToolHero';
import ToolCTA from '@/components/tools/ToolCTA';
import ScoreGauge from '@/components/tools/ScoreGauge';
import ProgressBar from '@/components/tools/ProgressBar';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface ProfileInput {
  headline: string;
  about: string;
  currentTitle: string;
  experience: string;
  skills: string;
  hasPhoto: boolean;
  hasCustomURL: boolean;
  recommendations: '0' | '1-2' | '3-5' | '6+';
}

interface SectionScore {
  score: number;
  label: string;
  findings: string[];
  suggestions: string[];
}

interface ReviewResult {
  overallScore: number;
  sections: {
    headline: SectionScore;
    about: SectionScore;
    experience: SectionScore;
    skills: SectionScore;
    completeness: SectionScore;
    keywords: SectionScore;
  };
}

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const ACTION_VERBS = [
  'achieved', 'built', 'created', 'delivered', 'designed', 'developed',
  'drove', 'earned', 'established', 'expanded', 'generated', 'grew',
  'implemented', 'improved', 'increased', 'initiated', 'launched', 'led',
  'managed', 'negotiated', 'optimized', 'orchestrated', 'pioneered',
  'produced', 'reduced', 'revamped', 'scaled', 'spearheaded',
  'streamlined', 'supervised', 'transformed', 'accelerated', 'automated',
  'collaborated', 'consolidated', 'cultivated', 'directed', 'eliminated',
  'engineered', 'executed', 'facilitated', 'founded', 'guided',
  'identified', 'influenced', 'leveraged', 'mentored', 'modernized',
  'overhauled', 'restructured', 'secured', 'strengthened', 'surpassed',
];

const METRIC_PATTERNS = [
  /\d+%/,
  /\$[\d,.]+[kmb]?/i,
  /\d+x/i,
  /\d+\+?\s*(users|customers|clients|employees|team members|engineers|people|accounts|partners)/i,
  /\d+\+?\s*(million|billion|thousand|hundred)/i,
  /\d+\+?\s*(projects|campaigns|initiatives|products|features)/i,
  /increased.*\d/i,
  /reduced.*\d/i,
  /grew.*\d/i,
  /saved.*\d/i,
  /improved.*\d/i,
  /generated.*\d/i,
  /revenue/i,
  /roi\b/i,
  /yoy\b/i,
  /year[\s-]over[\s-]year/i,
];

const INDUSTRY_KEYWORDS = [
  'leadership', 'strategy', 'management', 'analytics', 'data-driven',
  'cross-functional', 'stakeholder', 'agile', 'scrum', 'lean',
  'innovation', 'transformation', 'digital', 'cloud', 'saas',
  'b2b', 'b2c', 'enterprise', 'startup', 'growth', 'optimization',
  'automation', 'machine learning', 'artificial intelligence', 'ai',
  'product management', 'project management', 'ux', 'ui', 'design thinking',
  'customer success', 'revenue', 'pipeline', 'go-to-market', 'gtm',
  'roi', 'kpi', 'okr', 'metrics', 'performance', 'scalable',
  'devops', 'ci/cd', 'microservices', 'api', 'full-stack', 'frontend',
  'backend', 'mobile', 'react', 'python', 'javascript', 'typescript',
  'sql', 'aws', 'azure', 'gcp', 'kubernetes', 'docker',
  'marketing', 'seo', 'sem', 'content strategy', 'brand',
  'sales', 'business development', 'account management', 'crm',
  'finance', 'accounting', 'compliance', 'risk management',
  'supply chain', 'operations', 'logistics', 'procurement',
  'human resources', 'talent acquisition', 'diversity', 'inclusion',
  'consulting', 'advisory', 'research', 'analysis',
];

const CTA_PHRASES = [
  'reach out', 'connect', 'contact', 'message me', 'let\'s talk',
  'get in touch', 'email me', 'open to', 'looking for', 'available for',
  'let\'s connect', 'feel free', 'dm me', 'happy to chat',
  'interested in', 'passionate about', 'hire me',
];

const STORYTELLING_INDICATORS = [
  'passionate', 'driven', 'dedicated', 'committed', 'believe',
  'journey', 'mission', 'vision', 'impact', 'purpose',
  'love', 'thrive', 'enjoy', 'fascinated', 'inspired',
  'story', 'background', 'career', 'path', 'transitioned',
];

/* ------------------------------------------------------------------ */
/*  Scoring algorithms                                                 */
/* ------------------------------------------------------------------ */

function scoreHeadline(headline: string): SectionScore {
  const findings: string[] = [];
  const suggestions: string[] = [];
  let score = 0;

  const trimmed = headline.trim();
  if (!trimmed) {
    return {
      score: 0,
      label: 'Headline',
      findings: ['No headline provided.'],
      suggestions: ['Add a compelling headline that describes who you are and what you do.'],
    };
  }

  // Length check (ideal 60-120 chars)
  const len = trimmed.length;
  if (len >= 60 && len <= 120) {
    score += 35;
    findings.push(`Good length (${len} characters). The ideal range is 60-120 characters.`);
  } else if (len >= 40 && len < 60) {
    score += 20;
    findings.push(`Headline is ${len} characters -- a bit short. Aim for 60-120 characters to maximize visibility.`);
    suggestions.push('Expand your headline with more descriptive keywords or a value proposition.');
  } else if (len > 120 && len <= 160) {
    score += 20;
    findings.push(`Headline is ${len} characters -- slightly long. Some may be truncated in search results.`);
    suggestions.push('Trim your headline to under 120 characters so it displays fully in search results.');
  } else if (len < 40) {
    score += 10;
    findings.push(`Headline is only ${len} characters -- too short to be effective.`);
    suggestions.push('Your headline should be at least 60 characters. Include your role, specialty, and what makes you unique.');
  } else {
    score += 10;
    findings.push(`Headline is ${len} characters -- too long. Much of it will be hidden.`);
    suggestions.push('Keep your headline under 120 characters for maximum impact.');
  }

  // Check for just a job title (too generic)
  const words = trimmed.split(/\s+/);
  if (words.length <= 3) {
    findings.push('Your headline appears to be just a job title.');
    suggestions.push('Go beyond your job title. Add your specialty, key achievement, or value proposition (e.g., "Marketing Director | Driving 3x revenue growth through data-driven campaigns").');
  } else {
    score += 15;
  }

  // Check for pipe/bullet separators (good formatting practice)
  if (/[|]/.test(trimmed)) {
    score += 10;
    findings.push('Good use of separators to organize your headline.');
  } else {
    suggestions.push('Consider using pipe separators (|) to structure your headline into distinct value points.');
  }

  // Check for keywords
  const headlineLower = trimmed.toLowerCase();
  const keywordCount = INDUSTRY_KEYWORDS.filter((k) => headlineLower.includes(k)).length;
  if (keywordCount >= 3) {
    score += 25;
    findings.push(`Contains ${keywordCount} industry keywords -- excellent for search visibility.`);
  } else if (keywordCount >= 1) {
    score += 15;
    findings.push(`Contains ${keywordCount} industry keyword(s). Adding more will improve discoverability.`);
    suggestions.push('Include 2-3 industry-specific keywords that recruiters search for in your field.');
  } else {
    findings.push('No common industry keywords detected.');
    suggestions.push('Add keywords relevant to your industry (e.g., "SaaS", "Data Analytics", "Product Management") so recruiters can find you.');
  }

  // Value proposition check
  const valueIndicators = ['helping', 'enabling', 'driving', 'building', 'growing', 'transforming', 'delivering', 'specializing', 'expert in', 'focused on'];
  const hasValue = valueIndicators.some((v) => headlineLower.includes(v));
  if (hasValue) {
    score += 15;
    findings.push('Includes a value proposition -- this helps differentiate you from other profiles.');
  } else {
    suggestions.push('Add a value proposition. Instead of just listing titles, explain what you deliver (e.g., "Helping startups scale from 0 to 1M users").');
  }

  return { score: Math.min(100, score), label: 'Headline', findings, suggestions };
}

function scoreAbout(about: string): SectionScore {
  const findings: string[] = [];
  const suggestions: string[] = [];
  let score = 0;

  const trimmed = about.trim();
  if (!trimmed) {
    return {
      score: 0,
      label: 'About / Summary',
      findings: ['No summary provided.'],
      suggestions: ['Write a compelling About section. This is your elevator pitch and one of the most-read sections on LinkedIn.'],
    };
  }

  // Word count check (ideal 200-500 words)
  const wordCount = trimmed.split(/\s+/).filter(Boolean).length;
  if (wordCount >= 200 && wordCount <= 500) {
    score += 25;
    findings.push(`Good length (${wordCount} words). The ideal range is 200-500 words.`);
  } else if (wordCount >= 100 && wordCount < 200) {
    score += 15;
    findings.push(`Summary is ${wordCount} words -- a bit short. Aim for 200-500 words for maximum impact.`);
    suggestions.push('Expand your summary with more detail about your expertise, achievements, and what drives you.');
  } else if (wordCount > 500 && wordCount <= 800) {
    score += 15;
    findings.push(`Summary is ${wordCount} words -- slightly long. Consider trimming to keep readers engaged.`);
    suggestions.push('Tighten your summary to under 500 words. Focus on the most compelling details.');
  } else if (wordCount < 100) {
    score += 5;
    findings.push(`Summary is only ${wordCount} words -- too short to make an impact.`);
    suggestions.push('Your summary should be at least 200 words. Cover who you are, what you do, your key achievements, and what you are looking for.');
  } else {
    score += 10;
    findings.push(`Summary is ${wordCount} words -- quite long. Most readers will not finish it.`);
    suggestions.push('Cut your summary to under 500 words. Lead with the most important information.');
  }

  // First person check (LinkedIn best practice)
  const aboutLower = trimmed.toLowerCase();
  const firstPerson = /\b(i |i'm|i've|my |me )\b/.test(aboutLower);
  const thirdPerson = /\b(he |she |they |his |her |their )\b/.test(aboutLower);
  if (firstPerson && !thirdPerson) {
    score += 10;
    findings.push('Written in first person -- this feels more personal and engaging.');
  } else if (thirdPerson) {
    findings.push('Written in third person. LinkedIn summaries are more engaging in first person.');
    suggestions.push('Rewrite your summary in first person ("I lead..." instead of "She leads...") for a more authentic, approachable tone.');
  }

  // Keywords check
  const keywordCount = INDUSTRY_KEYWORDS.filter((k) => aboutLower.includes(k)).length;
  if (keywordCount >= 8) {
    score += 20;
    findings.push(`Contains ${keywordCount} industry keywords -- excellent for LinkedIn search ranking.`);
  } else if (keywordCount >= 4) {
    score += 12;
    findings.push(`Contains ${keywordCount} industry keywords. Adding more will boost your search visibility.`);
    suggestions.push('Weave in additional industry-specific keywords naturally throughout your summary.');
  } else if (keywordCount >= 1) {
    score += 5;
    findings.push(`Only ${keywordCount} industry keyword(s) found.`);
    suggestions.push('Include 5-10 relevant industry keywords to improve your ranking in LinkedIn search results.');
  } else {
    findings.push('No common industry keywords detected.');
    suggestions.push('Add keywords relevant to your role and industry so recruiters can find you through LinkedIn search.');
  }

  // Storytelling elements
  const storyCount = STORYTELLING_INDICATORS.filter((s) => aboutLower.includes(s)).length;
  if (storyCount >= 3) {
    score += 15;
    findings.push('Good storytelling elements -- your summary has personality and conveys your motivations.');
  } else if (storyCount >= 1) {
    score += 8;
    findings.push('Some storytelling elements present. Consider adding more to make your summary memorable.');
    suggestions.push('Share what motivates you, a pivotal career moment, or your professional philosophy to make your summary stand out.');
  } else {
    suggestions.push('Add storytelling elements. Explain why you do what you do -- this makes your profile memorable and human.');
  }

  // CTA check
  const hasCTA = CTA_PHRASES.some((c) => aboutLower.includes(c));
  if (hasCTA) {
    score += 15;
    findings.push('Includes a call to action -- great for encouraging connections and opportunities.');
  } else {
    suggestions.push('End your summary with a call to action (e.g., "Reach out if you want to discuss..." or "Open to consulting opportunities in...").');
  }

  // Paragraph structure
  const paragraphs = trimmed.split(/\n\s*\n/).filter((p) => p.trim().length > 0);
  if (paragraphs.length >= 3) {
    score += 10;
    findings.push(`Well-structured with ${paragraphs.length} paragraphs for easy reading.`);
  } else if (paragraphs.length === 2) {
    score += 5;
    findings.push('Only 2 paragraphs detected. Breaking your summary into 3-4 shorter paragraphs improves readability.');
    suggestions.push('Split your summary into 3-4 short paragraphs. Wall-of-text summaries get skipped.');
  } else {
    findings.push('Your summary appears to be a single block of text.');
    suggestions.push('Break your summary into 3-4 short paragraphs. Use white space to make it scannable on mobile.');
  }

  // Metrics in about
  const hasMetrics = METRIC_PATTERNS.some((p) => p.test(trimmed));
  if (hasMetrics) {
    score += 5;
    findings.push('Includes quantified achievements in your summary -- this adds credibility.');
  } else {
    suggestions.push('Include 1-2 key metrics or achievements in your summary (e.g., "grew revenue 40% YoY" or "managed a $5M budget").');
  }

  return { score: Math.min(100, score), label: 'About / Summary', findings, suggestions };
}

function scoreExperience(experience: string, currentTitle: string): SectionScore {
  const findings: string[] = [];
  const suggestions: string[] = [];
  let score = 0;

  const trimmed = experience.trim();
  if (!trimmed) {
    return {
      score: 0,
      label: 'Experience',
      findings: ['No experience content provided.'],
      suggestions: ['Add your work experience with detailed bullet points describing your responsibilities and achievements.'],
    };
  }

  // Check for bullet points or line breaks (structured content)
  const lines = trimmed.split(/\n/).filter((l) => l.trim().length > 0);
  const bulletLines = lines.filter((l) => /^[\s]*[-*]/.test(l) || /^[\s]*\d+[.)]\s/.test(l));

  if (bulletLines.length >= 5) {
    score += 20;
    findings.push(`${bulletLines.length} bullet points detected -- well-structured experience section.`);
  } else if (bulletLines.length >= 2) {
    score += 12;
    findings.push(`${bulletLines.length} bullet points found. More detailed bullets will strengthen your profile.`);
    suggestions.push('Aim for 3-5 bullet points per role, each highlighting a specific achievement or responsibility.');
  } else if (lines.length >= 3) {
    score += 8;
    findings.push('Experience appears to be in paragraph form rather than bullets.');
    suggestions.push('Reformat your experience using bullet points. Bullets are easier to scan and perform better on LinkedIn.');
  } else {
    score += 3;
    findings.push('Very little experience content provided.');
    suggestions.push('Expand your experience section significantly. Each role should have 3-5 detailed bullet points.');
  }

  // Action verbs check
  const expLower = trimmed.toLowerCase();
  const foundVerbs = ACTION_VERBS.filter((v) => expLower.includes(v));
  if (foundVerbs.length >= 8) {
    score += 25;
    findings.push(`Excellent use of action verbs (${foundVerbs.length} found): ${foundVerbs.slice(0, 5).join(', ')}, and more.`);
  } else if (foundVerbs.length >= 4) {
    score += 15;
    findings.push(`${foundVerbs.length} action verbs found: ${foundVerbs.join(', ')}.`);
    suggestions.push('Start more bullet points with strong action verbs like "Spearheaded," "Optimized," or "Delivered" to convey impact.');
  } else if (foundVerbs.length >= 1) {
    score += 8;
    findings.push(`Only ${foundVerbs.length} action verb(s) found.`);
    suggestions.push('Begin each bullet point with a powerful action verb. Avoid passive language like "Responsible for" -- instead say "Led," "Built," "Drove."');
  } else {
    findings.push('No strong action verbs detected.');
    suggestions.push('Start every bullet with an action verb (e.g., "Delivered," "Scaled," "Implemented"). This dramatically improves readability and impact.');
  }

  // Metrics check
  const metricMatches = METRIC_PATTERNS.filter((p) => p.test(trimmed));
  if (metricMatches.length >= 5) {
    score += 25;
    findings.push('Excellent use of metrics and quantified results throughout your experience.');
  } else if (metricMatches.length >= 3) {
    score += 18;
    findings.push(`${metricMatches.length} quantified results found. More numbers will make your experience even stronger.`);
    suggestions.push('Add specific metrics to more bullet points. Numbers catch recruiters\' eyes (e.g., "Increased conversion rate by 35%").');
  } else if (metricMatches.length >= 1) {
    score += 10;
    findings.push(`Only ${metricMatches.length} metric(s) found in your experience.`);
    suggestions.push('Quantify your achievements wherever possible. Use percentages, dollar amounts, team sizes, or timeframes to add credibility.');
  } else {
    findings.push('No quantified metrics found in your experience.');
    suggestions.push('Add numbers to your bullet points. "Managed social media" becomes "Managed social media for 5 brands, growing combined following by 150K in 6 months."');
  }

  // Current title alignment
  if (currentTitle.trim()) {
    const titleLower = currentTitle.trim().toLowerCase();
    if (expLower.includes(titleLower)) {
      score += 10;
      findings.push('Current job title appears in your experience -- good consistency.');
    } else {
      suggestions.push(`Make sure your current title "${currentTitle}" is clearly listed in your experience section.`);
    }
  }

  // Length check
  const wordCount = trimmed.split(/\s+/).filter(Boolean).length;
  if (wordCount >= 150) {
    score += 15;
    findings.push(`Experience section has ${wordCount} words -- good level of detail.`);
  } else if (wordCount >= 75) {
    score += 8;
    findings.push(`Experience section is ${wordCount} words -- could use more detail.`);
    suggestions.push('Expand your experience descriptions. Recruiters want to see specific achievements and scope of your work.');
  } else {
    score += 3;
    findings.push(`Experience section is only ${wordCount} words -- much too brief.`);
    suggestions.push('Your experience section needs significantly more detail. Describe your key projects, leadership scope, and measurable results.');
  }

  // Check for variety in sentence starters
  const starters = lines
    .map((l) => l.trim().replace(/^[-*]\s*/, '').split(/\s+/)[0]?.toLowerCase())
    .filter(Boolean);
  const uniqueStarters = new Set(starters);
  if (starters.length > 3 && uniqueStarters.size < starters.length * 0.5) {
    suggestions.push('Vary your sentence starters. Starting multiple bullets the same way makes your experience feel repetitive.');
  } else if (starters.length > 3 && uniqueStarters.size >= starters.length * 0.7) {
    score += 5;
    findings.push('Good variety in how you begin your bullet points.');
  }

  return { score: Math.min(100, score), label: 'Experience', findings, suggestions };
}

function scoreSkills(skillsInput: string): SectionScore {
  const findings: string[] = [];
  const suggestions: string[] = [];
  let score = 0;

  const trimmed = skillsInput.trim();
  if (!trimmed) {
    return {
      score: 0,
      label: 'Skills',
      findings: ['No skills provided.'],
      suggestions: ['Add your skills -- LinkedIn allows up to 50 skills. List at least 10 to improve your search ranking.'],
    };
  }

  // Parse skills
  const skills = trimmed
    .split(/[,;]/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

  const count = skills.length;

  // Count check (ideal 10+)
  if (count >= 15) {
    score += 35;
    findings.push(`${count} skills listed -- excellent. LinkedIn allows up to 50, and more skills means more search appearances.`);
  } else if (count >= 10) {
    score += 25;
    findings.push(`${count} skills listed -- good start. Consider adding more to maximize discoverability.`);
    suggestions.push('Add more skills to reach 15-20 total. Each skill is another way recruiters can find you.');
  } else if (count >= 5) {
    score += 15;
    findings.push(`Only ${count} skills listed. This limits how often you appear in recruiter searches.`);
    suggestions.push('Increase your skills to at least 10-15. Include both technical skills and soft skills relevant to your target roles.');
  } else {
    score += 5;
    findings.push(`Only ${count} skill(s) listed -- far too few.`);
    suggestions.push('Add at least 10-15 skills immediately. LinkedIn profiles with more skills get significantly more profile views.');
  }

  // Relevance check (match against industry keywords)
  const skillsLower = skills.map((s) => s.toLowerCase());
  const relevantCount = skillsLower.filter((s) =>
    INDUSTRY_KEYWORDS.some((k) => s.includes(k) || k.includes(s)),
  ).length;

  if (relevantCount >= 5) {
    score += 25;
    findings.push(`${relevantCount} of your skills match common industry keywords -- great for search visibility.`);
  } else if (relevantCount >= 2) {
    score += 15;
    findings.push(`${relevantCount} skills match common industry keywords.`);
    suggestions.push('Add more industry-standard skill names. Use the exact terms recruiters search for (e.g., "Project Management" not "Managing Projects").');
  } else {
    score += 5;
    findings.push('Few of your listed skills match common industry keywords.');
    suggestions.push('Use standard industry terminology for your skills. Check LinkedIn job postings in your field to see what skills are most requested.');
  }

  // Check for mix of hard and soft skills
  const softSkillIndicators = ['leadership', 'communication', 'teamwork', 'problem solving', 'critical thinking', 'collaboration', 'management', 'strategic', 'analytical', 'creative'];
  const hasSoftSkills = skillsLower.some((s) => softSkillIndicators.some((ind) => s.includes(ind)));
  const hasHardSkills = skillsLower.some((s) => !softSkillIndicators.some((ind) => s.includes(ind)));

  if (hasSoftSkills && hasHardSkills) {
    score += 20;
    findings.push('Good mix of hard and soft skills.');
  } else if (hasHardSkills && !hasSoftSkills) {
    score += 10;
    findings.push('All hard/technical skills -- no soft skills detected.');
    suggestions.push('Add 3-5 soft skills like "Leadership," "Cross-Functional Collaboration," or "Strategic Planning" for a balanced profile.');
  } else if (hasSoftSkills && !hasHardSkills) {
    score += 5;
    findings.push('Mostly soft skills detected -- missing technical or hard skills.');
    suggestions.push('Add specific technical skills, tools, and certifications relevant to your field.');
  }

  // Check for very generic skills
  const genericSkills = ['microsoft office', 'excel', 'powerpoint', 'word', 'email', 'typing', 'computer skills'];
  const genericFound = skillsLower.filter((s) => genericSkills.some((g) => s.includes(g)));
  if (genericFound.length > 0) {
    findings.push(`Some very generic skills detected: ${genericFound.join(', ')}. These don't differentiate you.`);
    suggestions.push('Replace generic skills like "Microsoft Office" with more specific, high-value skills. Every skill slot should work hard for you.');
  } else {
    score += 10;
  }

  // Duplicate check
  const lowerSet = new Set(skillsLower);
  if (lowerSet.size < skills.length) {
    findings.push('Duplicate skills detected. Remove duplicates to make room for additional unique skills.');
    suggestions.push('Remove duplicate skill entries and replace them with new, relevant skills.');
  } else {
    score += 10;
  }

  return { score: Math.min(100, score), label: 'Skills', findings, suggestions };
}

function scoreCompleteness(hasPhoto: boolean, hasCustomURL: boolean, recommendations: string): SectionScore {
  const findings: string[] = [];
  const suggestions: string[] = [];
  let score = 0;

  // Profile photo (huge impact)
  if (hasPhoto) {
    score += 35;
    findings.push('Profile photo present. Profiles with photos get up to 21x more views and 9x more connection requests.');
  } else {
    findings.push('No profile photo.');
    suggestions.push('Add a professional headshot immediately. This is the single biggest factor in whether someone clicks on your profile. Use good lighting, a clean background, and dress appropriately for your industry.');
  }

  // Custom URL
  if (hasCustomURL) {
    score += 25;
    findings.push('Custom LinkedIn URL set. This looks more professional in emails and on your resume.');
  } else {
    findings.push('No custom LinkedIn URL.');
    suggestions.push('Set a custom URL (linkedin.com/in/yourname). It looks far more professional than the default URL with random numbers, and it is easier to share.');
  }

  // Recommendations
  switch (recommendations) {
    case '6+':
      score += 40;
      findings.push('6+ recommendations -- outstanding. This provides strong social proof to recruiters and hiring managers.');
      break;
    case '3-5':
      score += 30;
      findings.push('3-5 recommendations -- solid social proof. Consider aiming for even more.');
      suggestions.push('Try to reach 6+ recommendations. Reach out to colleagues, managers, or clients who can speak to specific strengths.');
      break;
    case '1-2':
      score += 15;
      findings.push('1-2 recommendations -- a start, but not enough to make a strong impression.');
      suggestions.push('Aim for at least 3-5 recommendations. A good approach: write a recommendation for someone first, then ask them to reciprocate.');
      break;
    case '0':
    default:
      findings.push('No recommendations. This is a missed opportunity for social proof.');
      suggestions.push('Get at least 3 recommendations. Start by writing recommendations for colleagues -- most will reciprocate. Focus on people who can speak to your key strengths.');
      break;
  }

  return { score: Math.min(100, score), label: 'Completeness', findings, suggestions };
}

function scoreKeywordOptimization(input: ProfileInput): SectionScore {
  const findings: string[] = [];
  const suggestions: string[] = [];
  let score = 0;

  // Combine all text for analysis
  const allText = [
    input.headline,
    input.about,
    input.currentTitle,
    input.experience,
    input.skills,
  ]
    .join(' ')
    .toLowerCase();

  if (allText.trim().length < 20) {
    return {
      score: 0,
      label: 'Keyword Optimization',
      findings: ['Not enough content to analyze keyword optimization.'],
      suggestions: ['Fill in all profile sections to get a keyword optimization score.'],
    };
  }

  // Check industry keyword coverage
  const foundKeywords = INDUSTRY_KEYWORDS.filter((k) => allText.includes(k));
  const keywordCount = foundKeywords.length;

  if (keywordCount >= 20) {
    score += 35;
    findings.push(`Excellent keyword coverage: ${keywordCount} industry keywords found across your profile.`);
  } else if (keywordCount >= 12) {
    score += 25;
    findings.push(`Good keyword coverage: ${keywordCount} industry keywords found. Room to add more.`);
    suggestions.push('Identify 5-10 more keywords relevant to your target role and weave them naturally into your profile.');
  } else if (keywordCount >= 5) {
    score += 15;
    findings.push(`Moderate keyword coverage: ${keywordCount} industry keywords found.`);
    suggestions.push('Your profile needs more keywords to rank well in LinkedIn search. Research what terms recruiters in your field search for and include them.');
  } else {
    score += 5;
    findings.push(`Low keyword coverage: only ${keywordCount} industry keywords found.`);
    suggestions.push('Your profile is unlikely to appear in recruiter searches. Add industry-specific terminology throughout your headline, summary, and experience.');
  }

  // Check keyword distribution across sections
  const headlineKeywords = INDUSTRY_KEYWORDS.filter((k) => input.headline.toLowerCase().includes(k));
  const aboutKeywords = INDUSTRY_KEYWORDS.filter((k) => input.about.toLowerCase().includes(k));
  const expKeywords = INDUSTRY_KEYWORDS.filter((k) => input.experience.toLowerCase().includes(k));

  const sectionsWithKeywords = [
    headlineKeywords.length > 0,
    aboutKeywords.length > 0,
    expKeywords.length > 0,
    input.skills.trim().length > 0,
  ].filter(Boolean).length;

  if (sectionsWithKeywords >= 4) {
    score += 25;
    findings.push('Keywords are well-distributed across all major sections -- this helps with LinkedIn\'s search algorithm.');
  } else if (sectionsWithKeywords >= 3) {
    score += 18;
    findings.push(`Keywords appear in ${sectionsWithKeywords} of 4 sections.`);
    suggestions.push('Ensure keywords appear in ALL sections -- headline, summary, experience, and skills. LinkedIn weights keyword placement across sections.');
  } else if (sectionsWithKeywords >= 2) {
    score += 10;
    findings.push(`Keywords only appear in ${sectionsWithKeywords} sections.`);
    suggestions.push('Spread your keywords across all profile sections. Having keywords in your headline is especially important for search ranking.');
  } else {
    score += 3;
    findings.push('Keywords are concentrated in only one section or absent.');
    suggestions.push('Distribute relevant keywords across your headline, summary, experience, and skills sections for maximum search visibility.');
  }

  // Keyword repetition (good for ranking)
  const keywordFrequency: Record<string, number> = {};
  for (const kw of foundKeywords) {
    const regex = new RegExp(kw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
    const matches = allText.match(regex);
    keywordFrequency[kw] = matches ? matches.length : 0;
  }

  const repeatedKeywords = Object.entries(keywordFrequency)
    .filter(([, count]) => count >= 3)
    .map(([kw]) => kw);

  if (repeatedKeywords.length >= 5) {
    score += 20;
    findings.push(`${repeatedKeywords.length} keywords appear 3+ times across your profile -- this reinforces your expertise to LinkedIn's algorithm.`);
  } else if (repeatedKeywords.length >= 2) {
    score += 12;
    findings.push(`${repeatedKeywords.length} keywords appear multiple times. Repeating your most important keywords across sections strengthens your ranking.`);
    suggestions.push('Mention your top 5-6 target keywords at least 2-3 times across different sections (without keyword stuffing).');
  } else {
    score += 5;
    suggestions.push('Repeat your most important keywords naturally across multiple sections. LinkedIn ranks profiles higher when key terms appear in the headline, summary, AND experience.');
  }

  // Show top found keywords
  if (foundKeywords.length > 0) {
    const topKeywords = Object.entries(keywordFrequency)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([kw, count]) => `${kw} (${count}x)`)
      .join(', ');
    findings.push(`Top keywords in your profile: ${topKeywords}.`);
  }

  // Check for keyword stuffing
  const wordCount = allText.split(/\s+/).filter(Boolean).length;
  if (wordCount > 0 && keywordCount > 0) {
    const density = keywordCount / wordCount;
    if (density > 0.15) {
      score -= 10;
      findings.push('Warning: Keyword density may be too high. This can appear unnatural.');
      suggestions.push('Reduce keyword repetition -- your profile should read naturally. Focus on quality over quantity.');
    }
  }

  return { score: Math.min(100, Math.max(0, score)), label: 'Keyword Optimization', findings, suggestions };
}

/* ------------------------------------------------------------------ */
/*  Main review function                                               */
/* ------------------------------------------------------------------ */

function reviewProfile(input: ProfileInput): ReviewResult {
  const headline = scoreHeadline(input.headline);
  const about = scoreAbout(input.about);
  const experience = scoreExperience(input.experience, input.currentTitle);
  const skills = scoreSkills(input.skills);
  const completeness = scoreCompleteness(input.hasPhoto, input.hasCustomURL, input.recommendations);
  const keywords = scoreKeywordOptimization(input);

  // Weighted average
  const overallScore = Math.round(
    headline.score * 0.20 +
    about.score * 0.20 +
    experience.score * 0.25 +
    skills.score * 0.10 +
    completeness.score * 0.10 +
    keywords.score * 0.15,
  );

  return {
    overallScore: Math.min(100, Math.max(0, overallScore)),
    sections: {
      headline,
      about,
      experience,
      skills,
      completeness,
      keywords,
    },
  };
}

/* ------------------------------------------------------------------ */
/*  Section Result Card                                                */
/* ------------------------------------------------------------------ */

function SectionCard({ section }: { section: SectionScore }) {
  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-gray-900">{section.label}</h3>
        <span
          className={`text-lg font-extrabold px-3 py-1 rounded-full ${
            section.score >= 70
              ? 'bg-green-100 text-green-700'
              : section.score >= 40
                ? 'bg-amber-100 text-amber-700'
                : 'bg-red-100 text-red-700'
          }`}
        >
          {section.score}/100
        </span>
      </div>

      <ProgressBar value={section.score} label={section.label} showValue={false} />

      {/* Findings */}
      {section.findings.length > 0 && (
        <div className="mt-4">
          <h4 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-2">
            Findings
          </h4>
          <ul className="space-y-2">
            {section.findings.map((finding, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                <svg
                  className="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span>{finding}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Suggestions */}
      {section.suggestions.length > 0 && (
        <div className="mt-4">
          <h4 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-2">
            Suggestions
          </h4>
          <ul className="space-y-2">
            {section.suggestions.map((suggestion, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-gray-800">
                <svg
                  className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                  />
                </svg>
                <span>{suggestion}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const DEFAULT_INPUT: ProfileInput = {
  headline: '',
  about: '',
  currentTitle: '',
  experience: '',
  skills: '',
  hasPhoto: true,
  hasCustomURL: false,
  recommendations: '0',
};

export default function LinkedInReviewerContent() {
  const [input, setInput] = useState<ProfileInput>(DEFAULT_INPUT);
  const [result, setResult] = useState<ReviewResult | null>(null);
  const [hasReviewed, setHasReviewed] = useState(false);

  const handleReview = () => {
    const review = reviewProfile(input);
    setResult(review);
    setHasReviewed(true);
    // Scroll to results
    setTimeout(() => {
      document.getElementById('review-results')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handleReset = () => {
    setInput(DEFAULT_INPUT);
    setResult(null);
    setHasReviewed(false);
  };

  const canReview =
    input.headline.trim().length > 0 ||
    input.about.trim().length > 0 ||
    input.experience.trim().length > 0 ||
    input.skills.trim().length > 0;

  const updateField = <K extends keyof ProfileInput>(field: K, value: ProfileInput[K]) => {
    setInput((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-stone-100">
      <ToolHero
        title="LinkedIn Profile Reviewer"
        description="Score your LinkedIn profile section by section. Get actionable feedback on your headline, summary, experience, skills, and more -- all analyzed instantly in your browser."
        category="LinkedIn Tool"
      />

      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Input Section */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Enter Your LinkedIn Profile Details
          </h2>
          <p className="text-gray-600 mb-8">
            Paste the content from each section of your LinkedIn profile. The more sections you fill in, the more comprehensive your review will be.
          </p>

          <div className="space-y-6">
            {/* Headline */}
            <div>
              <label htmlFor="headline" className="block text-sm font-semibold text-gray-700 mb-1.5">
                Headline
              </label>
              <p className="text-xs text-gray-500 mb-2">
                The text that appears below your name on LinkedIn (e.g., &quot;Senior Product Manager | B2B SaaS | Driving Growth Through Data&quot;).
              </p>
              <input
                id="headline"
                type="text"
                value={input.headline}
                onChange={(e) => updateField('headline', e.target.value)}
                placeholder="Paste your LinkedIn headline here..."
                className="w-full border border-gray-300 rounded-xl px-4 py-3 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                maxLength={220}
              />
              <p className="text-xs text-gray-400 mt-1 text-right">
                {input.headline.length} / 220 characters
              </p>
            </div>

            {/* About / Summary */}
            <div>
              <label htmlFor="about" className="block text-sm font-semibold text-gray-700 mb-1.5">
                About / Summary
              </label>
              <p className="text-xs text-gray-500 mb-2">
                Copy and paste your entire About section from LinkedIn.
              </p>
              <textarea
                id="about"
                rows={8}
                value={input.about}
                onChange={(e) => updateField('about', e.target.value)}
                placeholder="Paste your LinkedIn About/Summary section here..."
                className="w-full border border-gray-300 rounded-xl px-4 py-3 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y text-sm leading-relaxed"
              />
            </div>

            {/* Current Job Title */}
            <div>
              <label htmlFor="current-title" className="block text-sm font-semibold text-gray-700 mb-1.5">
                Current Job Title
              </label>
              <p className="text-xs text-gray-500 mb-2">
                Your current role title as it appears on LinkedIn.
              </p>
              <input
                id="current-title"
                type="text"
                value={input.currentTitle}
                onChange={(e) => updateField('currentTitle', e.target.value)}
                placeholder="e.g., Senior Software Engineer at Google"
                className="w-full border border-gray-300 rounded-xl px-4 py-3 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>

            {/* Experience */}
            <div>
              <label htmlFor="experience" className="block text-sm font-semibold text-gray-700 mb-1.5">
                Experience Section
              </label>
              <p className="text-xs text-gray-500 mb-2">
                Paste the bullet points and descriptions from your experience entries.
              </p>
              <textarea
                id="experience"
                rows={10}
                value={input.experience}
                onChange={(e) => updateField('experience', e.target.value)}
                placeholder="Paste your experience bullets here, e.g.:&#10;- Led a team of 12 engineers to deliver a new platform that increased revenue by 40%&#10;- Managed a $2M annual budget while reducing costs by 15%&#10;- Spearheaded migration to cloud infrastructure, improving uptime to 99.99%"
                className="w-full border border-gray-300 rounded-xl px-4 py-3 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y text-sm leading-relaxed"
              />
            </div>

            {/* Skills */}
            <div>
              <label htmlFor="skills" className="block text-sm font-semibold text-gray-700 mb-1.5">
                Skills
              </label>
              <p className="text-xs text-gray-500 mb-2">
                List your LinkedIn skills, separated by commas.
              </p>
              <input
                id="skills"
                type="text"
                value={input.skills}
                onChange={(e) => updateField('skills', e.target.value)}
                placeholder="e.g., Product Management, Agile, SQL, Data Analytics, Leadership, Python"
                className="w-full border border-gray-300 rounded-xl px-4 py-3 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>

            {/* Toggles and selects row */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {/* Profile Photo Toggle */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Profile Photo?
                </label>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => updateField('hasPhoto', true)}
                    className={`flex-1 py-2.5 px-4 rounded-xl text-sm font-semibold border transition ${
                      input.hasPhoto
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    onClick={() => updateField('hasPhoto', false)}
                    className={`flex-1 py-2.5 px-4 rounded-xl text-sm font-semibold border transition ${
                      !input.hasPhoto
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    No
                  </button>
                </div>
              </div>

              {/* Custom URL Toggle */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Custom LinkedIn URL?
                </label>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => updateField('hasCustomURL', true)}
                    className={`flex-1 py-2.5 px-4 rounded-xl text-sm font-semibold border transition ${
                      input.hasCustomURL
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    onClick={() => updateField('hasCustomURL', false)}
                    className={`flex-1 py-2.5 px-4 rounded-xl text-sm font-semibold border transition ${
                      !input.hasCustomURL
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    No
                  </button>
                </div>
              </div>

              {/* Recommendations */}
              <div>
                <label htmlFor="recommendations" className="block text-sm font-semibold text-gray-700 mb-2">
                  Recommendations
                </label>
                <select
                  id="recommendations"
                  value={input.recommendations}
                  onChange={(e) => updateField('recommendations', e.target.value as ProfileInput['recommendations'])}
                  className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white"
                >
                  <option value="0">0 recommendations</option>
                  <option value="1-2">1-2 recommendations</option>
                  <option value="3-5">3-5 recommendations</option>
                  <option value="6+">6+ recommendations</option>
                </select>
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex flex-wrap items-center gap-3 mt-8">
            <button
              onClick={handleReview}
              disabled={!canReview}
              className="bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white font-bold py-3 px-8 rounded-xl shadow text-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Review My Profile
            </button>
            {hasReviewed && (
              <button
                onClick={handleReset}
                className="inline-flex items-center gap-2 px-6 py-3 border border-gray-300 rounded-xl text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                Start Over
              </button>
            )}
          </div>
        </div>

        {/* Results */}
        {hasReviewed && result && (
          <div id="review-results" className="space-y-8">
            {/* Overall Score Card */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <div className="flex flex-col items-center">
                <p className="text-sm font-semibold text-blue-600 uppercase tracking-wide mb-4">
                  LinkedIn Profile Score
                </p>
                <ScoreGauge
                  score={result.overallScore}
                  label="Profile Score"
                  size={220}
                />
                <p className="text-gray-600 mt-4 text-center max-w-md">
                  {result.overallScore >= 80
                    ? 'Your LinkedIn profile is in excellent shape. Fine-tune the details below to make it truly outstanding.'
                    : result.overallScore >= 60
                      ? 'Your profile is solid but has room for improvement. Focus on the lower-scoring sections below.'
                      : result.overallScore >= 40
                        ? 'Your profile needs work in several areas. Follow the suggestions below to significantly boost your visibility.'
                        : 'Your profile needs significant improvement. Work through each section below to transform your LinkedIn presence.'}
                </p>
              </div>
            </div>

            {/* Section Score Overview */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6">
                Section Breakdown
              </h3>
              <div className="space-y-5">
                <ProgressBar
                  value={result.sections.headline.score}
                  label="Headline"
                />
                <ProgressBar
                  value={result.sections.about.score}
                  label="About / Summary"
                />
                <ProgressBar
                  value={result.sections.experience.score}
                  label="Experience"
                />
                <ProgressBar
                  value={result.sections.skills.score}
                  label="Skills"
                />
                <ProgressBar
                  value={result.sections.completeness.score}
                  label="Completeness"
                />
                <ProgressBar
                  value={result.sections.keywords.score}
                  label="Keyword Optimization"
                />
              </div>
              <p className="text-xs text-gray-500 mt-4">
                Weights: Headline 20% | About 20% | Experience 25% | Skills 10% | Completeness 10% | Keywords 15%
              </p>
            </div>

            {/* Individual Section Cards */}
            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-gray-900">
                Detailed Section Analysis
              </h3>
              <SectionCard section={result.sections.headline} />
              <SectionCard section={result.sections.about} />
              <SectionCard section={result.sections.experience} />
              <SectionCard section={result.sections.skills} />
              <SectionCard section={result.sections.completeness} />
              <SectionCard section={result.sections.keywords} />
            </div>

            {/* Educational Section */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                LinkedIn Profile Optimization Guide
              </h3>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Your LinkedIn profile is your digital professional identity. Recruiters, hiring managers, potential clients, and professional contacts all form their first impression of you based on what they see on LinkedIn. A well-optimized profile can be the difference between getting discovered and getting overlooked.
                </p>

                <h4 className="text-lg font-bold text-gray-900 pt-2">
                  Why Your Headline Matters Most
                </h4>
                <p>
                  Your headline is the single most visible element of your profile. It appears in search results, connection requests, comments, and messages. LinkedIn gives you 220 characters -- use them all. Go beyond your job title and communicate your value proposition, specialty, or the results you deliver.
                </p>

                <h4 className="text-lg font-bold text-gray-900 pt-2">
                  Writing a Compelling About Section
                </h4>
                <ul className="list-disc pl-5 space-y-2">
                  <li>
                    <strong>Start with a hook:</strong> Your first two lines are visible before the &quot;See more&quot; fold. Make them count with an attention-grabbing statement.
                  </li>
                  <li>
                    <strong>Tell your story:</strong> Explain what you do, why you do it, and what results you deliver. Use first person for authenticity.
                  </li>
                  <li>
                    <strong>Include keywords:</strong> LinkedIn&apos;s search algorithm indexes your summary heavily. Include terms that recruiters in your field search for.
                  </li>
                  <li>
                    <strong>End with a CTA:</strong> Tell people how to reach you and what you are open to -- new roles, consulting, speaking, collaboration.
                  </li>
                </ul>

                <h4 className="text-lg font-bold text-gray-900 pt-2">
                  Experience That Gets Noticed
                </h4>
                <ul className="list-disc pl-5 space-y-2">
                  <li>
                    <strong>Lead with action verbs:</strong> Start each bullet with a strong verb like &quot;Spearheaded,&quot; &quot;Delivered,&quot; or &quot;Transformed.&quot;
                  </li>
                  <li>
                    <strong>Quantify everything:</strong> Numbers catch the eye. Use percentages, dollar amounts, team sizes, and timeframes.
                  </li>
                  <li>
                    <strong>Show impact, not duties:</strong> &quot;Managed social media&quot; becomes &quot;Grew Instagram following from 10K to 85K in 12 months, driving a 3x increase in website traffic.&quot;
                  </li>
                </ul>

                <h4 className="text-lg font-bold text-gray-900 pt-2">
                  Maximizing Your Skills Section
                </h4>
                <p>
                  LinkedIn allows up to 50 skills. Profiles with 5+ skills get up to 17x more profile views. List your most important skills first, since LinkedIn uses the top 3 for search. Get endorsements from colleagues to validate your expertise -- endorsed skills carry more weight.
                </p>

                <h4 className="text-lg font-bold text-gray-900 pt-2">
                  Profile Completeness Signals
                </h4>
                <ul className="list-disc pl-5 space-y-2">
                  <li>
                    <strong>Professional photo:</strong> Profiles with photos receive 21x more views and 9x more connection requests. Use a high-quality headshot with good lighting.
                  </li>
                  <li>
                    <strong>Custom URL:</strong> A clean URL like linkedin.com/in/yourname looks professional on business cards and resumes.
                  </li>
                  <li>
                    <strong>Recommendations:</strong> Written recommendations provide powerful social proof. Aim for at least 3-5 from managers, colleagues, or clients.
                  </li>
                </ul>

                <p className="mt-6 pt-4 border-t border-gray-200 text-sm text-gray-500">
                  <strong>Sources &amp; Further Reading:</strong>{' '}
                  <a href="https://www.linkedin.com/help/linkedin/answer/a522735" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">LinkedIn Help: Profile Best Practices</a>
                  {' · '}
                  <a href="https://blog.linkedin.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">LinkedIn Official Blog</a>
                  {' · '}
                  <a href="https://www.indeed.com/career-advice/resumes-cover-letters/linkedin-profile-tips" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">Indeed: LinkedIn Profile Tips</a>
                </p>
              </div>
            </div>

            {/* CTA */}
            <ToolCTA
              headline="Ready to level up your entire job search?"
              description="Superpower Resume helps you create tailored resumes and cover letters that match any job description -- so your applications are as strong as your LinkedIn profile."
              buttonText="Create Your Superpower Resume"
              buttonHref="https://app.superpowerresume.com/sign-up"
            />
          </div>
        )}

        {/* Show CTA even before reviewing */}
        {!hasReviewed && (
          <ToolCTA
            headline="Your LinkedIn profile is just the start"
            description="Superpower Resume creates tailored resumes and cover letters that match any job description, helping you land more interviews."
            buttonText="Get Started Free"
            buttonHref="https://app.superpowerresume.com/sign-up"
          />
        )}
      </div>
    </div>
  );
}
